import React, { useEffect, useState } from "react"
import axios from "axios";
import { trackPromise, usePromiseTracker } from "react-promise-tracker";
import { ThreeDots } from "react-loader-spinner";
import { useUserAuth } from "../../context/userAuthContext";
import list_of_months from "../quality_control_screens/resources/list_of_months";
import { FaCircle as Circle } from 'react-icons/fa'
import { LineChart, ComposedChart, BarChart, PieChart, Cell, Pie, Bar, Line, XAxis, ResponsiveContainer, YAxis, Tooltip, CartesianGrid, ReferenceLine, Label, Legend } from 'recharts'
import CountUp from 'react-countup'

const AgentKPIPerformance = ({
   permissions,
   setShowScreen
}) => {
   // Variables
   const daily_target = 4
   const daily_appliance_target = 5

   const { user, userDetails } = useUserAuth();
   const [stats, setStats] = useState('')
 
   // Data
   const [partnerList, setPartnerList] = useState([])
   const [agentList, setAgentList] = useState([])
 
   // Filters
   const [timeScopeFilter, setTimeScopeFilter] = useState('today')
   const [monthFilter, setMonthFilter] = useState('')
   const [dateFilter, setDateFilter] = useState({
      from: new Date().toISOString().substring(0, 10),
      to: new Date().toISOString().substring(0, 10)
   })
   const [partnerFilter, setPartnerFilter] = useState('')
   const [agentFilter, setAgentFilter] = useState('')
   
   // Get month options (last 3 months)
   const today = new Date()
   let monthOptions = []

   monthOptions.push(
      new Date().toISOString().substring(0, 7),
      new Date(today.setMonth(today.getMonth() - 1)).toISOString().substring(0, 7),
      new Date(today.setMonth(today.getMonth() - 1)).toISOString().substring(0, 7)
   )

   ///--------------------------

   const DataLoadingIcon = () => {
      const { promiseInProgress } = usePromiseTracker({ area: 'data_load'});

      return (
         promiseInProgress &&
         <React.Fragment>
            <ThreeDots type="rotatingLines" color="#03989e" height={80} width={80}/>
         </React.Fragment>
      )
   }

   const getPartnerList = async () => {
      await axios({
         method: 'get',
         url: 'https://api.appliancesure.com/two/admin/portal/partners/getPartnerList',
         params: {
            limit_data: true
         },
         headers: {
            Authorization: "Bearer " + user
         }
      })
      .then((value) => {
         if(value.data.success === true) {
            let partners = []

            for (let i = 0; i < value.data.records.length; i++) {
               const partner = value.data.records[i];
               
               partners.push(partner.partner_name)
            }

            setPartnerList(partners)
         }
      })
      .catch(() => {
         // Do nothing
      })
   }

   useEffect(() => {
      if(agentFilter === '') {
         if(permissions === 'global') {
            getPartnerList()
         } else {
            setPartnerList([userDetails.partner])
            setPartnerFilter(userDetails.partner)
         }

         if(permissions === 'individual') {
            setAgentFilter(userDetails.email_address)
            setAgentList([{
               first_name: userDetails.first_name,
               last_name: userDetails.last_name,
               email_address: userDetails.email_address
            }])
         }
      }
   }, [])

   const getAgentList = async () => {
      await axios({
         method: 'get',
         url: 'https://api.appliancesure.com/two/admin/portal/users/getUsersByPartner',
         params: {
            partner: partnerFilter
         },
         headers: {
            Authorization: "Bearer " + user
         }
      })
      .then( async (value) => {
         if(value.data.success === true) {
            let user_list = value.data.records;
            user_list.sort((a, b) => a.first_name.localeCompare(b.first_name))
            setAgentList(value.data.records)
         }
      })
   }

   useEffect(() => {
      if(permissions !== 'individual' && partnerFilter !== '') {
         getAgentList()
      }
   }, [partnerFilter])

   const getDashboardStats = () => {
      trackPromise(
         new Promise( async (resolve) => {
            await axios({
               method: 'get',
               url: 'https://api.appliancesure.com/two/admin/portal/reporting/agentKPIPerformance',
               headers: {
                  Authorization: "Bearer " + user
               },
               params: {
                  agent: agentFilter,
                  date_range: timeScopeFilter === 'month_select' ? { month: monthFilter.substring(0, 7) } : dateFilter
               }
            })
            .then((value) => {
               setTimeout(() => {
                  if(value.data.success === true) {
                     setStats({
                        ...value.data.data,
                        timeScope: timeScopeFilter
                     })
                  } else {
                     setStats("No data")
                  }

                  resolve("Yes")
               }, 0)
            })
            .catch(() => {
               alert("There was an error getting the requested data, please try again")
               resolve("Yes")
            })
         })
      , 'data_load')
   }

   useEffect(() => {
      if(agentFilter !== '' && timeScopeFilter !== '') {
         if(timeScopeFilter === 'date_select' && dateFilter === '') {
            return
         } else if (timeScopeFilter === 'month_select' && monthFilter === '') {
            return
         } else {
           getDashboardStats()
         }
      }
   }, [agentFilter, monthFilter, dateFilter, timeScopeFilter])

   const changeTimeScopeFilter = (event) => {
      const val = event.target.value;

      setTimeScopeFilter(val)

      let date = new Date()
      if(val === 'today') {
         setDateFilter({
            from: date.toISOString().substring(0, 10),
            to: date.toISOString().substring(0, 10)
         })
      } else if (val === 'yesterday') {
         date.setDate(date.getDate() - 1)
         setDateFilter({
            from: date.toISOString().substring(0, 10),
            to: date.toISOString().substring(0, 10)
         })
      } else if (val === 'last_7_days') {
         const today = date.toISOString().substring(0, 10)
         date.setDate(date.getDate() - 6)
         setDateFilter({
            from: date.toISOString().substring(0, 10),
            to: today
         })
      } else if (val === 'month_select') {
         setMonthFilter('')
      } else if (val === 'date_select') {
         setDateFilter('')
      }
   }

   //---------------------------------------
   //----- JSX GENERATION FOR TILES ------//
   //---------------------------------------

   //--- Tile #1 | Total sales
   const generateTotalSalesTile = () => {
      let jsx = []

      if(stats !== '') {
         let active_days = 0

         if(stats.timeScope === 'last_7_days' || stats.timeScope === 'month_select') {
            for (let i = 0; i < stats.sales_calls_over_time.length; i++) {
               const day = stats.sales_calls_over_time[i];
               
               if(day.calls > 0) {
                  active_days++
               }
            }
         } else {
            active_days = 1
         }

         const target = daily_target * active_days

         let percentage_completed = (stats.total_sales / target) * 100
         if(percentage_completed > 100) percentage_completed = 100
         if(percentage_completed === 0) percentage_completed = 5

         const chart_data = [
            {
               name: "sales",
               value: percentage_completed
            },
            {
               name: 'remainder',
               value: 100 - percentage_completed
            }
         ]

         let performance = 0
         
         if(stats.total_sales > 0) {
            performance = ((stats.total_sales / active_days).toFixed(2)).replaceAll(".00", "")
         }

         const colors = [percentage_completed === 100 ? '#7AC200' : percentage_completed >= 75 ? '#ffa500' : '#ff0000', '#FFF']

         jsx.push(
            <React.Fragment>
               <div className="chart-container" style={{marginTop: 10}}>
                  <ResponsiveContainer height={200} width="100%">
                     <PieChart width={200} height={200} margin={{ top: -30, right: 0, left: 0, bottom: 0 }}>
                        <Pie
                           dataKey="value"
                           data={chart_data}
                           innerRadius={65}
                           outerRadius={75}
                           isAnimationActive={true}
                           fill
                        >
                           {chart_data.map((entry, index) => (
                              <Cell key={`cell-${index}`} fill={colors[index]}/>
                           ))}
                           <Label id="center-pie-label" position="center" value={(percentage_completed === 5 ? 0 : percentage_completed.toFixed(2).replaceAll(".00", "")) + "%"}/>
                        </Pie>
                     </PieChart>
                  </ResponsiveContainer>

                  <p className={`quality-comparison`} style={{textAlign: 'center', marginTop: -25, fontSize: 24}}>
                     {performance} out of {daily_target} sales {percentage_completed === 100 ? '🚀' : percentage_completed >= 75 ? '💪' : '📉'}
                     {
                        active_days > 1 ? (
                           <React.Fragment>
                              <br/>
                              <br/>
                              <span style={{fontSize: 14, position: 'relative', top: -14, fontWeight: 'normal', marginBottom: -14}} className="quality-comparison-sub-heading">Average per day</span>
                           </React.Fragment>
                        ) : null
                     }
                  </p>
               </div>
            </React.Fragment>
         )
      }

      return jsx
   }

   //--- Tile #2 | Sales / Calls over time
   const generateSalesCallsOverTime = () => {
      let jsx = []

      const CustomLabel = ({ active, payload, label }) => {
         if(active && payload && payload.length) {

            return (
               <div className="custom-chart-label">
                  <p><span style={{color: '#03989e'}}>{payload[0].payload.sales} sales</span><br/><br/>
                  {payload[0].payload.calls} calls</p>
                  <p>{label}</p>
               </div>
            )
         }
      }

      if(stats !== '') {
         jsx.push(
            <React.Fragment>
               <div className="daily-scores-high-low">
                  <span>
                     <p style={{marginBottom: 0}} className="quality-comparison-sub-heading">Sales made</p>
                     <p className="quality-comparison">{stats.total_sales}</p>
                  </span>

                  <span>
                     <p style={{marginBottom: 0}} className="quality-comparison-sub-heading">Leads connected</p>
                     <p className="quality-comparison">{stats.total_calls}</p>
                  </span>
               </div>

               <div className="chart-container">
                  <ResponsiveContainer height={150} width="100%">
                     <ComposedChart data={stats.sales_calls_over_time} margin={{ top: 10, right: -20, left: -20, bottom: 0 }}>
                        <CartesianGrid strokeDasharray="4 4" />
                        <XAxis dataKey="interval"/>
                        <YAxis yAxisId="left" />
                        <YAxis yAxisId="right" orientation="right" />
                        <Bar dataKey="sales" fill="#03989e" maxBarSize={20} yAxisId="left"/>
                        <Line dataKey="calls" yAxisId="right" />
                        <Tooltip content={<CustomLabel/>} cursor={false}/>
                     </ComposedChart>
                  </ResponsiveContainer>
               </div>
            </React.Fragment>
         )
      }

      return jsx;
   }

   //--- Tile #3 | Conversion rate
   const generateConversionRateTile = () => {
      let jsx = []

      if(stats !== '') {
         jsx.push(
            <React.Fragment>
               <br/>
               <center>
                  <CountUp
                     start={0}
                     end={stats.conversion_rate.conversion}
                     decimals={2}
                     duration={0.5}
                     className="quality-score"
                     style={{fontSize: 50}}
                     suffix="%"
                  />
               </center>

               <div className="table-wrapper">
                  <table className="tariff-information-table">
                     <tbody>
                        <tr>
                           <td>Sales made</td>
                           <td>{stats.total_sales}</td>
                        </tr>
                        <tr>
                           <td>Answered calls</td>
                           <td>{stats.conversion_rate.call_count}</td>
                        </tr>
                        <tr>
                           <td>Unanswered calls</td>
                           <td>{stats.total_calls - stats.conversion_rate.call_count}</td>
                        </tr>
                        <tr>
                           <td>Answer rate</td>
                           <td>{((stats.conversion_rate.call_count / stats.total_calls) * 100).toFixed(2)}%</td>
                        </tr>
                     </tbody>
                  </table>
               </div>
            </React.Fragment>
         )
      }

      return jsx
   }

   //--- Tile #4 | Outcome summary
   const generateOutcomeSummaryTile = () => {
      let jsx = []

      if(stats !== '') {
         let category_jsx = {
            positive: [],
            neutral: [],
            negative: []
         }

         for (let i = 0; i < stats.outcome_summary.summary.length; i++) {
            const el = stats.outcome_summary.summary[i];
            
            category_jsx[el.category].push(
               <tr>
                  <td>{el.outcome}</td>
                  <td>{el.count}</td>
               </tr>
            )
         }

         const colors = ["#7AC200", "#fea501", '#FF0000']

         jsx.push(
            <React.Fragment>
               <br/>
               {/* Pie chart */}
               <div className="chart-container" style={{marginTop: 10}}>
                  <ResponsiveContainer height={200} width="100%">
                     <PieChart width={200} height={200} margin={{ top: -30, right: 0, left: 0, bottom: 0 }}>
                        <Pie
                           dataKey="count"
                           data={stats.outcome_summary.category_counts_array}
                           outerRadius={80}
                           isAnimationActive={true}
                           fill
                        >
                           {stats.outcome_summary.category_counts_array.map((entry, index) => (
                              <Cell key={`percentage-cell-${index}`} fill={colors[index]}/>
                           ))}
                        </Pie>
                     </PieChart>
                  </ResponsiveContainer>
               </div>

               {/* Explantory table */}
               <div className="table-wrapper">
                  <table className="tariff-information-table">
                     <tbody>
                        <tr>
                           <th colSpan={2} style={{color: '#7AC200'}}>Positive</th>
                        </tr>
                        {category_jsx.positive}
                        
                        <tr>
                           &nbsp;
                        </tr>

                        <tr>
                           <th colSpan={2} style={{color: '#fea501'}}>Neutral</th>
                        </tr>
                        {category_jsx.neutral}

                        <tr>
                           &nbsp;
                        </tr>

                        <tr>
                           <th colSpan={2} style={{color: 'red'}}>Negative</th>
                        </tr>
                        {category_jsx.negative}
                     </tbody>
                  </table>
               </div>
            </React.Fragment>
         )
      }

      return jsx
   }

   //--- Tile #5 | Product breakdown
   const generateProductBreakdownTile = () => {
      let jsx = []

      if(stats !== '') {
         const colors = ["#03989e", "#00c2cb", "#7AC200", "#4caf50", '#424242']

         jsx.push(
            <React.Fragment>
               <br/>

               {
                  stats.total_sales === 0 ? (
                     <p style={{marginTop: -10}}>No data to display</p>
                  ) : null
               }

               {/* Pie chart */}
               <div className="chart-container" style={{marginTop: 10}}>
                  <ResponsiveContainer height={200} width="100%">
                     <PieChart width={200} height={200} margin={{ top: -30, right: 0, left: 0, bottom: 0 }}>
                        <Pie
                           dataKey="count"
                           data={stats.product_breakdown.product_count}
                           outerRadius={80}
                           isAnimationActive={true}
                           fill
                        >
                           {stats.product_breakdown.product_count.map((entry, index) => (
                              <Cell key={`percentage-cell-${index}`} fill={colors[index]}/>
                           ))}
                        </Pie>
                     </PieChart>
                  </ResponsiveContainer>
               </div>

               

               {
                  stats.product_breakdown.product_count.map((item, index) => (
                     <React.Fragment>
                     
                        <h5 style={{margin: 0}}><Circle style={{color: colors[index]}}/> {item.product}</h5>
                        <p style={{marginTop: 5}} className="disclaimer">Count: {item.count} ({((item.count / stats.total_sales) * 100).toFixed(2)}%)</p>
                     
                     </React.Fragment>
                  ))
               }

               <div className="table-wrapper">
                  <table className="tariff-information-table">
                     <tbody>
                        <tr>
                           <th colSpan={2} style={{color: colors[0]}}>Appliances</th>
                        </tr>

                        {
                           stats.total_sales === 0 ? (
                              <p>No data to display</p>
                           ) : null
                        }

                        {
                           stats.product_breakdown.appliance_count.map(item => (
                              <tr>
                                 <td>{item.appliance}</td>
                                 <td>{item.count}</td>
                              </tr>
                           ))
                        }
                     </tbody>
                  </table>
               </div>
            </React.Fragment>
         )
      }

      return jsx
   }

   //--- Tile #6 | Appliance count
   const generateApplianceCountTile = () => {
      let jsx = []

      if(stats !== '') {
         let active_days = 0

         if(stats.timeScope === 'last_7_days' || stats.timeScope === 'month_select') {
            for (let i = 0; i < stats.sales_calls_over_time.length; i++) {
               const day = stats.sales_calls_over_time[i];
               
               if(day.calls > 0) {
                  active_days++
               }
            }
         } else {
            active_days = 1
         }

         const target = daily_appliance_target * active_days

         jsx.push(
            <React.Fragment>
               <br/>
               <center>
                  <CountUp
                     start={0}
                     end={stats.appliance_count}
                     duration={0.5}
                     className="quality-score"
                     style={{fontSize: 50}}
                  />
               </center>

               <div className="table-wrapper">
                  <table className="tariff-information-table">
                     <tbody>
                        <tr>
                           <td>Products</td>
                           <td>{stats.total_sales}</td>
                        </tr>
                        <tr>
                           <td>Appliances</td>
                           <td>{stats.appliance_count}</td>
                        </tr>
                        <tr>
                           <td>Target</td>
                           <td>{target}</td>
                        </tr>
                        <tr>
                           <td>Appliances per product</td>
                           <td>{stats.appliance_count > 0 ? (stats.appliance_count / stats.total_sales).toFixed(2) : 0}</td>
                        </tr>
                     </tbody>
                  </table>
               </div>
            </React.Fragment>
         )
      }

      return jsx
   }

   //--- Tile #7 | Go live rate
   const generateGoLiveRateTile = () => {
      let jsx = []

      if(stats !== '') {
         jsx.push(
            <React.Fragment>
               <br/>
               <center>
                  <CountUp
                     start={0}
                     end={stats.go_live_rate.go_live_rate}
                     duration={0.5}
                     decimals={2}
                     className="quality-score"
                     style={{fontSize: 50}}
                     suffix="%"
                  />
               </center>

               <div className="table-wrapper">
                  <table className="tariff-information-table">
                     <tbody>
                        <tr>
                           <td>Active sales</td>
                           <td>{stats.go_live_rate.active_sales}</td>
                        </tr>
                        <tr>
                           <td>Live sales</td>
                           <td>{stats.go_live_rate.live_sales}</td>
                        </tr>
                        <tr>
                           <td>Pending sales</td>
                           <td>{stats.go_live_rate.pending_sales}</td>
                        </tr>
                        <tr>
                           <td>Cancelled sales</td>
                           <td>{stats.go_live_rate.lapsed_sales}</td>
                        </tr>
                     </tbody>
                  </table>
               </div>
            </React.Fragment>
         )
      }

      return jsx
   }

   const { promiseInProgress } = usePromiseTracker({ area: 'data_load'});

   return (
      <div className="screen-inner-container">
         <button className="back-button" onClick={() => setShowScreen('')}>Back to reporting</button>
         <h2>Agent KPI Performance</h2>

         <br/>
         <br/>

         {
            stats !== '' ? (
               <button disabled={promiseInProgress} style={{marginBottom: 10}} onClick={getDashboardStats} className="plain-text-button refresh">Refresh data</button> 
            ) : null
         }
         
         <div className="qa-dashboard-grid-container">
            {/* FILTER BAR */}
            <div className="qa-dashboard-filter-bar" id="bar">
               {/* Left hand side of bar */}
               <ul className="filter-bar-list left">
                  <li>
                     {/* Company filter */}
                     <select
                        className="qa-dashboard-filter-select"
                        value={partnerFilter}
                        onChange={(e) => setPartnerFilter(e.target.value)}
                        disabled={promiseInProgress}
                     >
                        <option value="" selected disabled>Choose a company</option>
                        {
                           partnerList.map(partner => (
                              <option value={partner}>{partner}</option>
                           ))
                        }
                     </select>
                  </li>
                  <li>
                     {/* Agent filter */}
                     <select
                        className="qa-dashboard-filter-select"
                        value={agentFilter}
                        onChange={(e) => setAgentFilter(e.target.value)}
                        disabled={promiseInProgress}
                     >
                        <option value="" selected disabled>Choose an agent</option>
                        {
                           agentList.map(agent => (
                              <option value={agent.email_address}>{agent.first_name} {agent.last_name}</option>
                           ))
                        }
                     </select>
                  </li>
               </ul>

               {/* Right hand side of bar */}
               <ul className="filter-bar-list right">
                  <li>
                     <select 
                        className="qa-dashboard-filter-select"
                        value={timeScopeFilter}
                        onChange={changeTimeScopeFilter}
                        disabled={promiseInProgress}
                     >
                        <option value="" disabled>Select a time scope</option>
                        <option value="today">Today</option>
                        <option value="yesterday">Yesterday</option>
                        <option value="last_7_days">Last 7 days</option>
                        <option value="date_select">Date select</option>
                        <option value="month_select">Month select</option>
                     </select>
                  </li>

                  {
                     timeScopeFilter === 'date_select' ? (
                        <li>
                           <input
                              type="date"
                              className="qa-dashboard-filter-select"
                              value={dateFilter.from}
                              onChange={(e) => setDateFilter({ from: e.target.value, to: e.target.value })}
                              disabled={promiseInProgress}
                           />
                        </li>
                     ) : null
                  }

                  {
                     timeScopeFilter === 'month_select' ? (
                        <li>
                           <select
                              className="qa-dashboard-filter-select"
                              value={monthFilter}
                              onChange={(e) => setMonthFilter( e.target.value )}
                              disabled={promiseInProgress}
                           >
                              <option value="" selected disabled>Select a month</option>
                              {monthOptions.map(option => (
                                 <option value={option}>
                                    {list_of_months[parseInt(option.split("-")[1]) - 1]} {option.split("-")[0]}
                                 </option>
                              ))}
                           </select>
                        </li>
                     ) : null
                  }
               </ul>
            </div>

            {/*----------------------------------------
            --------------- TILE JSX ------------------
            ----------------------------------------*/}

            {/* Total sales */}
            <div className={`qa-dashboard-tile ${promiseInProgress || stats === 'No data' || stats === '' ? 'loading' : ''}`} id="tile1">
               {
                  promiseInProgress ? (
                     <DataLoadingIcon/>
                  ) : stats === '' ? (
                     <p className="no-data-loader">Select filters</p>
                  ) : stats === 'No data' ? (
                     <p className="no-data-loader">No data</p>
                  ) : (
                     <React.Fragment>
                        <h4>Sales KPI</h4>

                        {generateTotalSalesTile()}
                     </React.Fragment>
                  )
               }
            </div>

            {/* Sales / calls over time */}
            <div className={`qa-dashboard-tile ${promiseInProgress || stats === 'No data' || stats === '' ? 'loading' : ''}`} id="tile2">
               {
                  promiseInProgress ? (
                     <DataLoadingIcon/>
                  ) : stats === '' ? (
                     <p className="no-data-loader">Select filters</p>
                  ) : stats === 'No data' ? (
                     <p className="no-data-loader">No data</p>
                  ) : (
                     <React.Fragment>
                        <h4>Performance over time</h4>

                        {generateSalesCallsOverTime()}
                     </React.Fragment>
                  )
               }
            </div>

            {/* Conversion rate */}
            <div className={`qa-dashboard-tile ${promiseInProgress || stats === 'No data' || stats === '' ? 'loading' : ''}`} id="tile3">
               {
                  promiseInProgress ? (
                     <DataLoadingIcon/>
                  ) : stats === '' ? (
                     <p className="no-data-loader">Select filters</p>
                  ) : stats === 'No data' ? (
                     <p className="no-data-loader">No data</p>
                  ) : (
                     <React.Fragment>
                        <h4>Conversion rate</h4>

                        {generateConversionRateTile()}
                     </React.Fragment>
                  )
               }
            </div>

            {/* Call outcome breakdown */}
            <div className={`qa-dashboard-tile double-height ${promiseInProgress || stats === 'No data' || stats === '' ? 'loading' : ''}`} id="tile4">
               {
                  promiseInProgress ? (
                     <DataLoadingIcon/>
                  ) : stats === '' ? (
                     <p className="no-data-loader">Select filters</p>
                  ) : stats === 'No data' ? (
                     <p className="no-data-loader">No data</p>
                  ) : (
                     <React.Fragment>
                        <h4>Outcome summary</h4>

                        {generateOutcomeSummaryTile()}
                     </React.Fragment>
                  )
               }
            </div>

            {/* Product breakdown */}
            <div className={`qa-dashboard-tile double-height ${promiseInProgress || stats === 'No data' || stats === '' ? 'loading' : ''}`} id="tile5">
               {
                  promiseInProgress ? (
                     <DataLoadingIcon/>
                  ) : stats === '' ? (
                     <p className="no-data-loader">Select filters</p>
                  ) : stats === 'No data' ? (
                     <p className="no-data-loader">No data</p>
                  ) : (
                     <React.Fragment>
                        <h4>Product breakdown</h4>

                        {generateProductBreakdownTile()}
                     </React.Fragment>
                  )
               }
            </div>

            {/* Number of appliances */}
            <div className={`qa-dashboard-tile ${promiseInProgress || stats === 'No data' || stats === '' ? 'loading' : ''}`} id="tile6">
               {
                  promiseInProgress ? (
                     <DataLoadingIcon/>
                  ) : stats === '' ? (
                     <p className="no-data-loader">Select filters</p>
                  ) : stats === 'No data' ? (
                     <p className="no-data-loader">No data</p>
                  ) : (
                     <React.Fragment>
                        <h4>Appliance count</h4>

                        {generateApplianceCountTile()}
                     </React.Fragment>
                  )
               }
            </div>

            {/* Go live rates */}
            <div className={`qa-dashboard-tile ${promiseInProgress || stats === 'No data' || stats === '' ? 'loading' : ''}`} id="tile7">
               {
                  promiseInProgress ? (
                     <DataLoadingIcon/>
                  ) : stats === '' ? (
                     <p className="no-data-loader">Select filters</p>
                  ) : stats === 'No data' ? (
                     <p className="no-data-loader">No data</p>
                  ) : (
                     <React.Fragment>
                        <h4>Go live rate</h4>

                        {generateGoLiveRateTile()}
                     </React.Fragment>
                  )
               }
            </div>
         </div>
      </div>
   )
}

export default AgentKPIPerformance